// Composer Studio — 공용 패널 레이아웃 셸.
//
// 보고서 에디터·작성 페이지 편집이 공유하는 N-패널 레이아웃 셸.
// 기능:
//   1) 방향 토글  : 좌중우(row) ↔ 상중하(col)
//   2) 순서 변경  : 패널 헤더 화살표 버튼(grip은 시각 hint)
//   3) 리사이즈   : 인접 패널 사이 splitter 드래그(orientation 축 따라 가로/세로)
//   4) compact 접기: renderCompact 지정 패널만 — 리사이즈 최소폭까지 줄이거나 접기 버튼을 누르면
//                    세로 아이콘 띠(renderCompact, 접혀도 아이콘으로 사용 가능)로 전환. 펴기는
//                    renderCompact가 받는 api.onExpand. renderCompact 없는 패널은 접기 없이 리사이즈만.
//   5) 영속       : orientation/panelOrder/sizes → localStorage
//   6) 호스트 옵션 : lockOrientation(row 고정·툴바 생략) / 패널 chromeless(헤더 없이 본문만) /
//                    defaultCompact(저장분 없으면 접힘 시작) / ref 핸들 expand·collapse +
//                    onCompactChange — 페이지 레벨 2분할(양식 탐색기 레일 | 탭 영역, 2026-09-20)용.
//                    전부 opt-in이라 기존 3분할 호스트는 무변.
//
// 순수 프레젠테이션 + 로컬 state. 외부 상태에 의존하지 않는다(재사용/테스트 용이).
// (구 헤더-only 접기[본문 숨기고 제목바만 남김]는 제거 — compact 하나로 통합. 2026-07-09)
// admin은 CSR이므로 localStorage 직접 접근(try/catch 가드).
//
// ⚠️ 순서 변경은 dnd-kit 대신 화살표 버튼 — 리사이즈 splitter의 pointer 드래그와 이벤트 충돌이 없다.

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ScrollHintArea } from './ScrollHintArea';

export interface EditorPanel {
  id: string;
  title: string; // 패널 헤더 라벨
  content: React.ReactNode;
  /**
   * 셸의 패널 헤더(⠿ 제목·순서 화살표·접기)와 ScrollHintArea 없이 content만 그린다 — 자기 헤더와
   * 스크롤을 스스로 갖는 패널(페이지 레벨 양식 탐색기 레일·탭 영역, 2026-09-20). 접기는 호스트가
   * `EditorPanelLayoutHandle.collapse`로, 펴기는 renderCompact의 api.onExpand 또는 handle.expand로.
   */
  chromeless?: boolean;
  /**
   * 저장분에 이 패널의 폭이 없을 때 compact(접힘)로 시작한다(renderCompact 있는 패널만 의미).
   * 기본 접힘 레일(양식 탐색기)용 — 저장분이 있으면 그 값이 우선이라 사용자의 펼침이 유지된다.
   */
  defaultCompact?: boolean;
  /**
   * compact(아이콘 레일) 렌더 — 지정 시 이 패널은 "폭 연속선상 접기"로 동작한다(row 방향 한정).
   * 리사이즈 최소폭 = compactWidthPx로 제한되고, 폭 ≤ compactThresholdPx이면 헤더/본문 대신 이
   * 렌더로 전환된다(접혀도 아이콘으로 사용 가능, 자체 펴기 버튼은 api.onExpand). 미지정 패널은
   * 접기 없이 리사이즈만.
   */
  renderCompact?: (api: { onExpand: () => void }) => React.ReactNode;
  /** compact(아이콘 레일) 폭 px. 기본 44. 이 패널의 리사이즈 최소폭이기도 함. */
  compactWidthPx?: number;
  /** 이 폭 이하로 줄면 compact 렌더로 전환. 기본 compactWidthPx*2. */
  compactThresholdPx?: number;
  /**
   * 기본/시작 폭 px — (1) 저장된 사이즈가 없을 때의 초기 flex-basis, (2) 펴기(onExpand) 복원 폭.
   * 지정 시 이 패널은 flex-1 균등이 아니라 이 폭으로 시작한다(팔레트/속성 등 고정폭 사이드). 미지정
   * 패널은 flex-1(캔버스 등 나머지 채움). expandWidthOf 기본값 max(200, threshold*2).
   */
  defaultWidthPx?: number;
  /**
   * 기본/시작 폭 % — 저장 사이즈·defaultWidthPx가 없을 때의 초기 flex-basis(row 방향 한정).
   * 컨테이너 비율 기반 시작폭(예: 좌 25% / 우 25% → 가운데 flex-1이 50%). 리사이즈 시작 시
   * 실측 px로 확정되므로 이후엔 px 저장 규약 그대로.
   */
  defaultWidthPct?: number;
}

export interface EditorPanelLayoutProps {
  panels: EditorPanel[]; // 기본 순서(id 유니크)
  storageKey: string; // 영속 키(localStorage: `${storageKey}:panel-layout`)
  minPanelPx?: number; // 리사이즈 최소(기본 120, compact 패널은 compactWidthPx)
  /**
   * 방향 토글(좌중우/상중하)을 이 DOM 노드로 포탈 렌더 — 지정 시 자체 상단 툴바 행을 생략해
   * 세로 공간을 아낀다(외부 행에 이식, 예: 작성 탭 반응형 뷰 스위처 행 맨뒤). 미지정(기본)이면
   * 기존처럼 자체 행 렌더 — 다른 사용처(양식 탭 등)는 무변. 상태는 여전히 내부(포탈은 UI만 이동).
   */
  toolbarPortalEl?: HTMLElement | null;
  /**
   * 방향을 row(좌중우)로 고정 — 방향 토글 툴바 행을 아예 그리지 않고(포탈도 없음) 저장분의
   * orientation도 무시한다. 페이지 레벨 2분할(탐색기 레일 | 탭 영역)처럼 상중하가 의미 없는 호스트용.
   */
  lockOrientation?: 'row';
  /**
   * compact(접힘) 상태가 바뀔 때 접힌 패널 id 목록을 통지 — 호스트가 토글 버튼의 눌림 표시 등
   * 반응형 UI를 그릴 때 쓴다(상태 정본은 여전히 셸 내부 sizes).
   */
  onCompactChange?: (compactIds: string[]) => void;
}

/** 호스트가 ref로 쓰는 명령 API — 단축키(Ctrl+K 펼침·Esc 접힘)·헤더 토글 버튼용. */
export interface EditorPanelLayoutHandle {
  /** 패널을 기본 폭으로 편다(renderCompact 없는 패널도 defaultWidthPx 복원). */
  expand: (id: string) => void;
  /** renderCompact 있는 패널을 compact 폭으로 접는다(없는 패널은 무시). */
  collapse: (id: string) => void;
}

type Orientation = 'row' | 'col';

// localStorage에 직렬화되는 영속 상태.
interface PersistedLayout {
  orientation: Orientation;
  panelOrder: string[];
  // 패널 id → flex-basis(px). 미지정 패널은 flex-1로 균등 신축.
  sizes: Record<string, number>;
}

const DEFAULT_MIN_PANEL_PX = 120;
const DEFAULT_COMPACT_PX = 44;

// admin 톤 공통 버튼(작은 버튼, text-xs, gray 계열 — 툴바/헤더와 일관).
const HEADER_BTN =
  'inline-flex items-center justify-center h-5 w-5 rounded border border-gray-300 ' +
  'bg-white text-gray-600 text-xs leading-none transition-colors ' +
  'hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:bg-white';

const TOOLBAR_BTN =
  'inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border ' +
  'border-gray-300 bg-white text-gray-700 transition-colors hover:bg-gray-50';

function storageKeyFor(storageKey: string): string {
  return `${storageKey}:panel-layout`;
}

// 저장된 레이아웃을 안전하게 로드. 파싱 실패/부재 시 null(→ 기본값).
function loadPersisted(storageKey: string): Partial<PersistedLayout> | null {
  try {
    const raw = window.localStorage.getItem(storageKeyFor(storageKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedLayout>;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function savePersisted(storageKey: string, layout: PersistedLayout): void {
  try {
    window.localStorage.setItem(storageKeyFor(storageKey), JSON.stringify(layout));
  } catch {
    // 쿼터 초과/사파리 프라이빗 모드 등 — 영속 실패는 무해.
  }
}

// 저장분 sizes를 현존 패널·유효 px만 남기고 정규화(lazy init과 storageKey 재로드가 공유).
export function sanitizeSizes(
  saved: Partial<PersistedLayout> | null,
  defaultIds: string[],
): Record<string, number> {
  const known = new Set(defaultIds);
  const out: Record<string, number> = {};
  for (const [id, px] of Object.entries(saved?.sizes ?? {})) {
    if (known.has(id) && typeof px === 'number' && px > 0) out[id] = px;
  }
  return out;
}

/**
 * 저장분에 폭이 없는 `defaultCompact` 패널을 compact 폭으로 채운다(순수) — 첫 방문·저장분 소실 시
 * 접힘으로 시작하되, 사용자가 한 번이라도 펼쳐 저장된 폭이 있으면 그 값을 존중한다.
 */
export function applyDefaultCompact(
  sizes: Record<string, number>,
  panels: readonly EditorPanel[],
): Record<string, number> {
  let out = sizes;
  for (const p of panels) {
    if (!p.defaultCompact || !p.renderCompact || sizes[p.id] != null) continue;
    if (out === sizes) out = { ...sizes };
    out[p.id] = compactWidthOf(p);
  }
  return out;
}

// 기본 순서에서 저장된 순서를 정규화: 저장분 중 현존 패널만 유지 + 신규 패널을 끝에 보강.
function normalizeOrder(defaultIds: string[], saved?: string[]): string[] {
  if (!saved || saved.length === 0) return [...defaultIds];
  const known = new Set(defaultIds);
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of saved) {
    if (known.has(id) && !seen.has(id)) {
      result.push(id);
      seen.add(id);
    }
  }
  for (const id of defaultIds) {
    if (!seen.has(id)) result.push(id);
  }
  return result;
}

// ── compact 파라미터 헬퍼(순수) ──
function compactWidthOf(p: EditorPanel): number {
  return p.compactWidthPx ?? DEFAULT_COMPACT_PX;
}
function compactThresholdOf(p: EditorPanel): number {
  return p.compactThresholdPx ?? compactWidthOf(p) * 2;
}
function expandWidthOf(p: EditorPanel): number {
  return p.defaultWidthPx ?? Math.max(200, compactThresholdOf(p) * 2);
}

// ── 잔여 폭 흡수 주체(순수) ────────────────────────────────────────────────
// 모든 패널이 폭을 명시하면(스플리터를 한 번만 당겨도 **인접 두 패널**의 sizes가 저장되므로 금방
// 그렇게 된다) 전부 flex-grow:0이라 컨테이너가 넓어질 때 남는 폭이 **빈 띠로 남는다**
// (2026-07-22 사용자 제보 — 우측 패널 옆 여백).
// 흡수 주체는 **원래 유연하게 설계된 패널**(defaultWidthPx/Pct 미지정 = 가운데 캔버스). "마지막
// 패널"로 두면 창을 넓힐 때 속성 패널이 늘어나 설계 의도와 반대가 된다(2026-07-22 리뷰 적발).
// 그 패널이 compact로 접혀 있으면 흡수할 수 없으므로 마지막 비compact 패널로 폴백한다.
export function resolveAbsorbTarget(args: {
  orderedPanels: EditorPanel[];
  sizes: Record<string, number>;
  isRow: boolean;
}): { absorbIdx: number; hasFlexPanel: boolean } {
  const { orderedPanels, sizes, isRow } = args;
  const isCompactAt = (p: EditorPanel): boolean => {
    const w = sizes[p.id];
    return isRow && !!p.renderCompact && w != null && w <= compactThresholdOf(p);
  };
  const flexibleIdx = orderedPanels.findIndex(
    (p) => p.defaultWidthPx == null && p.defaultWidthPct == null,
  );
  let idx = flexibleIdx;
  if (idx < 0 || isCompactAt(orderedPanels[idx])) {
    // 폴백: 뒤에서부터 첫 비compact 패널.
    idx = -1;
    for (let k = orderedPanels.length - 1; k >= 0; k -= 1) {
      if (!isCompactAt(orderedPanels[k])) {
        idx = k;
        break;
      }
    }
  }
  // 이미 basis 없이 flex-1로 렌더되는 패널이 있으면 그쪽이 잔여를 먹으므로 grow 불필요.
  // (col 방향은 모든 패널이 flex-1 균등이라 항상 true — 잔여 개념 자체가 없다.)
  const anyFlex = orderedPanels.some((p) => {
    const w = sizes[p.id];
    if (w != null) return false;
    if (!isRow) return true;
    return p.defaultWidthPx == null && p.defaultWidthPct == null;
  });
  return { absorbIdx: idx, hasFlexPanel: anyFlex };
}

/**
 * 패널 하나의 flex/minWidth 결정(순수 — DOM 측정 의존 없음).
 * - 폭: 사용자 지정(sizes) 우선 → 없으면 defaultWidthPx → defaultWidthPct(%) → flex-1.
 *   (팔레트/속성처럼 초기 고정폭이 필요한 패널은 defaultWidthPx/Pct로 시작폭 지정.)
 *   ⚠️ 폭 시맨틱이라 col(상중하) 방향에선 flex-basis가 height가 되어 오적용됨 → row(좌중우)일 때만
 *   초기폭으로 쓰고, col에선 flex-1 균등(사용자 지정 sizes는 축 전환 시 초기화됨).
 * - grow: 잔여 폭 흡수 주체(absorbIdx)만 1 — resolveAbsorbTarget 참조.
 * - shrink=1 + minWidth: 창을 **좁힐** 때 sizes 합 > 컨테이너가 되면 shrink:0에선 패널이 컨테이너
 *   밖으로 밀려 잘린다(grow는 확대만 처리 — 2026-07-22 리뷰 적발). 최소폭까지 비례 양보하게 두고,
 *   그 이하로는 minPanelPx(compact 패널은 compactWidthPx)로 방어한다.
 */
export function resolvePanelFlex(args: {
  panel: EditorPanel;
  index: number;
  sizes: Record<string, number>;
  isRow: boolean;
  minPanelPx: number;
  absorbIdx: number;
  hasFlexPanel: boolean;
}): { flex: string; minWidth: number } {
  const { panel, index, sizes, isRow, minPanelPx, absorbIdx, hasFlexPanel } = args;
  const rawWidth = sizes[panel.id];
  const canCompact = isRow && !!panel.renderCompact;
  const isCompact = canCompact && rawWidth != null && rawWidth <= compactThresholdOf(panel);
  const width = rawWidth ?? (isRow ? panel.defaultWidthPx : undefined);
  const widthPct = rawWidth == null && width == null && isRow ? panel.defaultWidthPct : undefined;
  const grow = isRow && index === absorbIdx && !isCompact && !hasFlexPanel ? 1 : 0;
  const minPx = isCompact ? compactWidthOf(panel) : minPanelPx;
  // compact: compactWidthPx 고정. 그 외: 폭 지정 시 basis 고정(px→%), 없으면 flex-1 균등.
  if (isCompact) return { flex: `0 0 ${compactWidthOf(panel)}px`, minWidth: 0 };
  if (width != null) return { flex: `${grow} 1 ${width}px`, minWidth: isRow ? minPx : 0 };
  if (widthPct != null) return { flex: `${grow} 1 ${widthPct}%`, minWidth: isRow ? minPx : 0 };
  return { flex: '1 1 0', minWidth: 0 };
}

export const EditorPanelLayout = forwardRef<EditorPanelLayoutHandle, EditorPanelLayoutProps>(
  function EditorPanelLayout(
    {
      panels,
      storageKey,
      minPanelPx = DEFAULT_MIN_PANEL_PX,
      toolbarPortalEl,
      lockOrientation,
      onCompactChange,
    },
    ref,
  ): JSX.Element {
  const defaultIds = useMemo(() => panels.map((p) => p.id), [panels]);
  const panelById = useMemo(() => {
    const map = new Map<string, EditorPanel>();
    for (const p of panels) map.set(p.id, p);
    return map;
  }, [panels]);

  // 초기 상태: 저장분 복원(없으면 기본값). lazy init로 mount 1회만 읽는다.
  // lockOrientation이면 저장분의 col도 무시(row 고정).
  const [orientation, setOrientation] = useState<Orientation>(() => {
    if (lockOrientation) return lockOrientation;
    const saved = loadPersisted(storageKey);
    return saved?.orientation === 'col' ? 'col' : 'row';
  });
  const [panelOrder, setPanelOrder] = useState<string[]>(() => {
    const saved = loadPersisted(storageKey);
    return normalizeOrder(defaultIds, saved?.panelOrder);
  });
  const [sizes, setSizes] = useState<Record<string, number>>(() =>
    applyDefaultCompact(sanitizeSizes(loadPersisted(storageKey), defaultIds), panels),
  );

  // storageKey가 mount 후에 바뀌는 호스트 대응 — 양식 탭 셸은 key가 `...:${template.key}`인데
  // 새로고침 직후엔 template이 초기 샘플('untitled')이라 그 키로 lazy init된 뒤, 템플릿 복원이
  // 끝나면 실제 키로 바뀐다. lazy init은 mount 1회라 상태가 옛 키 기준(기본값)으로 남고, 아래
  // 저장 effect가 그 기본값을 **새 키에 200ms 후 덮어써** 접기/리사이즈 저장분을 매 새로고침마다
  // 지워 왔다(2026-08-04 실사고 — 전 키 sizes가 {}). 키가 바뀌면 그 키의 저장분으로 상태를
  // 교체한다. 저장 effect보다 먼저 선언되어야 한다 — setState 재렌더가 저장 디바운스(200ms)를
  // 새 상태로 재예약해 기본값 커밋을 대체한다.
  const prevStorageKeyRef = useRef(storageKey);
  useEffect(() => {
    if (prevStorageKeyRef.current === storageKey) return;
    prevStorageKeyRef.current = storageKey;
    const saved = loadPersisted(storageKey);
    setOrientation(lockOrientation ?? (saved?.orientation === 'col' ? 'col' : 'row'));
    setPanelOrder(normalizeOrder(defaultIds, saved?.panelOrder));
    setSizes(applyDefaultCompact(sanitizeSizes(saved, defaultIds), panels));
  }, [storageKey, defaultIds, panels, lockOrientation]);

  // panels prop이 바뀌어 순서/사이즈에 사라진 id가 남으면 정리(신규는 끝에 보강).
  useEffect(() => {
    setPanelOrder((prev) => {
      const next = normalizeOrder(defaultIds, prev);
      return next.length === prev.length && next.every((id, i) => id === prev[i]) ? prev : next;
    });
    const known = new Set(defaultIds);
    setSizes((prev) => {
      const entries = Object.entries(prev).filter(([id]) => known.has(id));
      return entries.length === Object.keys(prev).length ? prev : Object.fromEntries(entries);
    });
  }, [defaultIds]);

  // 상태 변경 시 영속 — 리사이즈 splitter 드래그는 pointermove마다 sizes를 갱신하므로, 매 프레임
  // 동기 localStorage write(JSON.stringify+setItem)로 인한 jank를 피하려 디바운스한다(드래그 종료 후 1회).
  // orientation/panelOrder/접기·펴기 등 저빈도 변경도 같은 200ms 후 커밋(무해).
  const saveTimerRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      savePersisted(storageKey, { orientation, panelOrder, sizes });
    }, 200);
    return () => window.clearTimeout(saveTimerRef.current);
  }, [storageKey, orientation, panelOrder, sizes]);

  // 실제 렌더 순서(현존 패널만).
  const orderedPanels = useMemo(
    () => panelOrder.map((id) => panelById.get(id)).filter((p): p is EditorPanel => Boolean(p)),
    [panelOrder, panelById],
  );

  const isRow = orientation === 'row';

  // 잔여 폭 흡수 주체 — 규칙은 resolveAbsorbTarget(순수)이 정본.
  // map 콜백 안에서 계산하면 패널 수만큼 반복(O(n²))이라 여기서 1회만.
  const { absorbIdx, hasFlexPanel } = useMemo(
    () => resolveAbsorbTarget({ orderedPanels, sizes, isRow }),
    [orderedPanels, sizes, isRow],
  );

  // ── 방향 토글 ────────────────────────────────────────────────────────────
  // 방향이 바뀌면 px 크기의 축이 달라져 의미가 없어지므로 sizes를 초기화(flex-1 균등 복귀).
  const toggleOrientation = useCallback(() => {
    setOrientation((prev) => (prev === 'row' ? 'col' : 'row'));
    setSizes({});
  }, []);

  // ── 순서 변경 ──────────────────────────────────────────────────────────────
  const movePanel = useCallback((id: string, dir: -1 | 1) => {
    setPanelOrder((prev) => {
      const idx = prev.indexOf(id);
      const target = idx + dir;
      if (idx < 0 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }, []);

  // ── compact(접기/펴기) — row 방향 + renderCompact 있는 패널만 ────────────────
  // 접기 = 폭을 compactWidthPx로 스냅(임계 이하 → compact 렌더). 펴기 = 기본 폭 복원.
  const minPxFor = useCallback(
    (id: string) => {
      const p = panelById.get(id);
      return isRow && p?.renderCompact ? compactWidthOf(p) : minPanelPx;
    },
    [panelById, isRow, minPanelPx],
  );

  const collapseToCompact = useCallback(
    (id: string) => {
      const p = panelById.get(id);
      if (!p?.renderCompact) return;
      setSizes((prev) => ({ ...prev, [id]: compactWidthOf(p) }));
    },
    [panelById],
  );

  const expandPanel = useCallback(
    (id: string) => {
      const p = panelById.get(id);
      if (!p) return;
      setSizes((prev) => ({ ...prev, [id]: expandWidthOf(p) }));
    },
    [panelById],
  );

  // 호스트 명령 API(단축키·헤더 토글) — 상태 정본은 sizes 그대로, 접기/펴기 함수만 노출.
  useImperativeHandle(
    ref,
    () => ({ expand: expandPanel, collapse: collapseToCompact }),
    [expandPanel, collapseToCompact],
  );

  // compact 집합 변화 통지 — id 목록을 문자열로 접어 같은 집합이면 재통지하지 않는다
  // (구분자 '|' — 패널 id는 kebab/단어라 포함되지 않는다).
  const compactKey = isRow
    ? panels
        .filter((p) => {
          const w = sizes[p.id];
          return !!p.renderCompact && w != null && w <= compactThresholdOf(p);
        })
        .map((p) => p.id)
        .join('|')
    : '';
  const onCompactChangeRef = useRef(onCompactChange);
  onCompactChangeRef.current = onCompactChange;
  useEffect(() => {
    onCompactChangeRef.current?.(compactKey === '' ? [] : compactKey.split('|'));
  }, [compactKey]);

  // ── 리사이즈 ──────────────────────────────────────────────────────────────
  // 패널 DOM 참조(사이즈 측정용). splitter 드래그 시작 시 인접 두 패널의 실제 크기를
  // 측정해 시작 px로 확정하고, delta를 prev += / next -= 로 적용(각 패널 min 이상일 때만).
  const panelRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const setPanelRefEl = useCallback(
    (id: string) => (el: HTMLDivElement | null) => {
      if (el) panelRefs.current.set(id, el);
      else panelRefs.current.delete(id);
    },
    [],
  );

  const dragState = useRef<{
    prevId: string;
    nextId: string;
    startPos: number;
    startPrev: number;
    startNext: number;
  } | null>(null);

  const onSplitterPointerDown = useCallback(
    (prevId: string, nextId: string) => (e: React.PointerEvent<HTMLDivElement>) => {
      const prevEl = panelRefs.current.get(prevId);
      const nextEl = panelRefs.current.get(nextId);
      if (!prevEl || !nextEl) return;
      const prevRect = prevEl.getBoundingClientRect();
      const nextRect = nextEl.getBoundingClientRect();
      dragState.current = {
        prevId,
        nextId,
        startPos: isRow ? e.clientX : e.clientY,
        startPrev: isRow ? prevRect.width : prevRect.height,
        startNext: isRow ? nextRect.width : nextRect.height,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [isRow],
  );

  const onSplitterPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const st = dragState.current;
      if (!st) return;
      const pos = isRow ? e.clientX : e.clientY;
      const delta = pos - st.startPos;
      // 각 패널의 최소 크기(compact 패널은 compactWidthPx)를 개별 적용.
      const minPrev = minPxFor(st.prevId);
      const minNext = minPxFor(st.nextId);
      let nextPrev = st.startPrev + delta;
      let nextNext = st.startNext - delta;
      if (nextPrev < minPrev) {
        const over = minPrev - nextPrev;
        nextPrev = minPrev;
        nextNext -= over;
      }
      if (nextNext < minNext) {
        const over = minNext - nextNext;
        nextNext = minNext;
        nextPrev -= over;
      }
      if (nextPrev < minPrev || nextNext < minNext) return;
      setSizes((prev) => ({ ...prev, [st.prevId]: nextPrev, [st.nextId]: nextNext }));
    },
    [isRow, minPxFor],
  );

  const onSplitterPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    dragState.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }, []);

  // 방향 토글(+패널 수) — 기본은 자체 상단 행, toolbarPortalEl 지정 시 그 노드로 포탈(행 생략).
  const toolbarContent = (
    <>
      <button
        type="button"
        className={TOOLBAR_BTN}
        onClick={toggleOrientation}
        title={isRow ? '상중하 배치로 전환' : '좌중우 배치로 전환'}
        aria-label={isRow ? '상중하 배치로 전환' : '좌중우 배치로 전환'}
      >
        <span aria-hidden>{isRow ? '⬍' : '⬌'}</span>
        <span>{isRow ? '좌중우' : '상중하'}</span>
      </button>
      <span className="text-[11px] text-gray-400">패널 {orderedPanels.length}개</span>
    </>
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {/* 상단 툴바: 방향 토글 — 포탈 지정 시 외부 행으로 이동(세로 공간 절약). 방향 고정이면 생략. */}
      {lockOrientation ? null : toolbarPortalEl ? (
        createPortal(toolbarContent, toolbarPortalEl)
      ) : (
        <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-2 py-1">
          {toolbarContent}
        </div>
      )}

      {/* 패널 컨테이너 */}
      <div className={`flex min-h-0 flex-1 ${isRow ? 'flex-row' : 'flex-col'}`}>
        {orderedPanels.map((panel, i) => {
          const rawWidth = sizes[panel.id];
          const canCompact = isRow && !!panel.renderCompact;
          const isCompact = canCompact && rawWidth != null && rawWidth <= compactThresholdOf(panel);
          // 폭/grow/최소폭 결정은 resolvePanelFlex(순수)가 정본.
          const { flex, minWidth } = resolvePanelFlex({
            panel,
            index: i,
            sizes,
            isRow,
            minPanelPx,
            absorbIdx,
            hasFlexPanel,
          });
          const panelStyle: React.CSSProperties = { flex, minWidth, minHeight: 0 };

          // splitter: 다음 패널이 있으면 항상 표시(compact 패널도 드래그로 펴기 가능).
          const nextPanel = orderedPanels[i + 1];
          const showSplitter = !!nextPanel;

          return (
            <div key={panel.id} className="contents">
              <div
                ref={setPanelRefEl(panel.id)}
                className={`flex flex-col overflow-hidden border-gray-200 ${
                  isRow ? 'border-r last:border-r-0' : 'border-b last:border-b-0'
                }`}
                style={panelStyle}
              >
                {isCompact && panel.renderCompact ? (
                  // compact 렌더(아이콘 레일) — 접혀도 사용 가능. 펴기는 renderCompact가 api.onExpand로.
                  <div className="min-h-0 flex-1 overflow-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {panel.renderCompact({ onExpand: () => expandPanel(panel.id) })}
                  </div>
                ) : panel.chromeless ? (
                  // chromeless — 헤더·ScrollHintArea 없이 본문이 패널을 채운다(자체 헤더/스크롤 보유).
                  <div className="flex min-h-0 flex-1 flex-col">{panel.content}</div>
                ) : (
                  <>
                    {/* 패널 헤더(얇게) — grip + 제목 + 순서변경 + (compact 가능 시) 접기 */}
                    <div className="flex items-center gap-1 border-b border-gray-200 bg-gray-50 px-1.5 py-1">
                      <span
                        className="cursor-default select-none text-gray-300"
                        aria-hidden
                        title="순서 변경은 화살표 버튼 사용"
                      >
                        ⠿
                      </span>
                      <span className="flex-1 truncate text-xs font-medium text-gray-700">
                        {panel.title}
                      </span>
                      {/* 순서 변경 */}
                      <button
                        type="button"
                        className={HEADER_BTN}
                        onClick={() => movePanel(panel.id, -1)}
                        disabled={i === 0}
                        title={isRow ? '왼쪽으로 이동' : '위로 이동'}
                        aria-label={isRow ? '왼쪽으로 이동' : '위로 이동'}
                      >
                        {isRow ? '←' : '↑'}
                      </button>
                      <button
                        type="button"
                        className={HEADER_BTN}
                        onClick={() => movePanel(panel.id, 1)}
                        disabled={i === orderedPanels.length - 1}
                        title={isRow ? '오른쪽으로 이동' : '아래로 이동'}
                        aria-label={isRow ? '오른쪽으로 이동' : '아래로 이동'}
                      >
                        {isRow ? '→' : '↓'}
                      </button>
                      {/* 접기(아이콘 띠) — renderCompact 있는 패널만 */}
                      {canCompact && (
                        <button
                          type="button"
                          className={HEADER_BTN}
                          onClick={() => collapseToCompact(panel.id)}
                          title="접기(아이콘 띠)"
                          aria-label="접기(아이콘 띠)"
                        >
                          «
                        </button>
                      )}
                    </div>

                    {/* 패널 본문 — 스크롤바 숨김 + 스크롤 가능 방향에 ▲/▼ chevron 힌트 */}
                    <ScrollHintArea>{panel.content}</ScrollHintArea>
                  </>
                )}
              </div>

              {/* 인접 패널 사이 splitter */}
              {showSplitter && (
                <div
                  role="separator"
                  aria-orientation={isRow ? 'vertical' : 'horizontal'}
                  className={`shrink-0 bg-gray-200 transition-colors hover:bg-brand-300 ${
                    isRow ? 'w-1 cursor-col-resize' : 'h-1 cursor-row-resize'
                  }`}
                  onPointerDown={onSplitterPointerDown(panel.id, nextPanel.id)}
                  onPointerMove={onSplitterPointerMove}
                  onPointerUp={onSplitterPointerUp}
                  onPointerCancel={onSplitterPointerUp}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
  },
);
